import React, {useMemo, useState} from 'react'
import {
  parseFleetDprDlPositionToDecimals,
  sanitizeCoordinatePart,
  validateDprShipPositionFinal,
} from '../../../shared/utils/coordinateParsing'
import DprMiniMap from './DprMiniMap'
import SearchableSelect from '../../../shared/components/SearchableSelect'

export interface VesselStatusPositionFields {
  startLatDeg: string
  startLatMin: string
  startLatSec: string
  startLatDir: 'N' | 'S'
  startLonDeg: string
  startLonMin: string
  startLonSec: string
  startLonDir: 'E' | 'W'
}

type VesselStatusPositionErrors = Partial<Record<keyof VesselStatusPositionFields, string>>

export const blankVesselStatusPositionFields = (): VesselStatusPositionFields => ({
  startLatDeg: '',
  startLatMin: '',
  startLatSec: '',
  startLatDir: 'N',
  startLonDeg: '',
  startLonMin: '',
  startLonSec: '',
  startLonDir: 'E',
})

export const buildVesselStatusPositionValue = (fields: VesselStatusPositionFields): string => {
  const visibleValues = [
    fields.startLatDeg,
    fields.startLatMin,
    fields.startLatSec,
    fields.startLonDeg,
    fields.startLonMin,
    fields.startLonSec,
  ].map((value) => String(value).trim())
  const hasAnyValue = visibleValues.some((value) => value !== '')
  if (!hasAnyValue) return ''

  const formatCoord = (label: string, deg: string, min: string, sec: string, dir: string) =>
    `${label}: ${deg.trim() || '--'} Deg ${min.trim() || '--'} min ${sec.trim() || '--'} sec ${dir}`

  return [
    formatCoord('Latitude', fields.startLatDeg, fields.startLatMin, fields.startLatSec, fields.startLatDir),
    formatCoord('Longitude', fields.startLonDeg, fields.startLonMin, fields.startLonSec, fields.startLonDir),
  ].join(' | ')
}

interface VesselStatusProps {
  vesselOptions: string[]
  selectedVessel: string
  onVesselChange: (vessel: string) => void
  selectedStatus: string
  onStatusChange: (status: string) => void
  isOffHire: boolean
  onOffHireChange: (checked: boolean) => void
  reportDate: string
  onDateChange: (date: string) => void
  positionLocation: string
  onPositionLocationChange: (location: string) => void
  positionTime: string
  onPositionTimeChange: (time: string) => void
  positionFields: VesselStatusPositionFields
  onPositionFieldsChange: React.Dispatch<React.SetStateAction<VesselStatusPositionFields>>
  showErrors?: boolean
  isVesselUser?: boolean
}

const VESSEL_STATUS_OPTIONS = [
  'Operational',
  'Breakdown',
  'Laid up',
  'Drydocked'
]

export const VesselStatus: React.FC<VesselStatusProps> = ({
  vesselOptions,
  selectedVessel,
  onVesselChange,
  selectedStatus,
  onStatusChange,
  isOffHire,
  onOffHireChange,
  reportDate,
  onDateChange,
  positionLocation,
  onPositionLocationChange,
  positionTime,
  onPositionTimeChange,
  positionFields,
  onPositionFieldsChange,
  showErrors = false,
  isVesselUser = false,
}) => {
  const [positionErrors, setPositionErrors] = useState<VesselStatusPositionErrors>({})

  const currentPositionString = useMemo(
    () => buildVesselStatusPositionValue(positionFields),
    [positionFields]
  )
  const positionDecimals = useMemo(
    () => parseFleetDprDlPositionToDecimals(currentPositionString),
    [currentPositionString]
  )

  const handlePositionFieldChange = (
    field: keyof VesselStatusPositionFields,
    value: VesselStatusPositionFields[keyof VesselStatusPositionFields]
  ) => {
    const nextVal =
      field === 'startLatDir' || field === 'startLonDir'
        ? value
        : sanitizeCoordinatePart(String(value))

    onPositionFieldsChange((prev) => ({
      ...prev,
      [field]: nextVal as never,
    }))

    setPositionErrors((prev) => {
      if (!prev[field]) return prev
      const next = {...prev}
      delete next[field]
      return next
    })
  }

  const handlePositionBlur = () => {
    if (!currentPositionString) {
      setPositionErrors({})
      return
    }

    setPositionErrors(validateDprShipPositionFinal(positionFields))
  }

  return (
    <div className='p-8'>
      <div className='card card-flush shadow-sm'>
        <div className='card-header'>
          <h3 className='card-title fw-bolder'>1. Ships Location</h3>
        </div>
        <div className='card-body py-5'>
          <div className='row mb-10'>
            <div className='col-md-6'>
              <label className='form-label required fw-bolder text-dark'>Report Date :</label>
              <input
                type='date'
                className={`form-control form-control-solid ${showErrors && !reportDate ? 'border-danger' : ''}`}
                value={reportDate}
                onChange={(e) => onDateChange(e.target.value)}
              />
            </div>
            <div className='col-md-6'>
              <label className='form-label required fw-bolder text-dark'>Vessel Name:</label>
              {isVesselUser ? (
                <input
                  type='text'
                  className='form-control form-control-solid bg-secondary'
                  value={selectedVessel}
                  disabled
                />
              ) : (
                <SearchableSelect
                  className={`form-select form-select-solid ${showErrors && !selectedVessel ? 'border-danger' : ''}`}
                  value={selectedVessel}
                  onChange={(e) => onVesselChange(e.target.value)}
                >
                  <option value=''>Select Vessel...</option>
                  {vesselOptions.map((v) => (
                    <option key={v} value={v}>
                      {v}
                    </option>
                  ))}
                </SearchableSelect>
              )}
            </div>
          </div>
          
          <div className='row mb-10'>
             <div className='col-md-6'>
              <label className='form-label required fw-bolder text-dark'>Vessel Status:</label>
              <SearchableSelect
                className={`form-select form-select-solid ${showErrors && !selectedStatus ? 'border-danger' : ''}`}
                value={selectedStatus}
                onChange={(e) => onStatusChange(e.target.value)}
              >
                <option value=''>Select Status...</option>
                {VESSEL_STATUS_OPTIONS.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </SearchableSelect>
             </div>
             <div className='col-md-6 d-flex align-items-end'>
              <div className='form-check form-check-custom form-check-solid'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  id='offHireCheckbox'
                  checked={isOffHire}
                  onChange={(e) => onOffHireChange(e.target.checked)}
                />
                <label className='form-check-label fw-bolder text-dark' htmlFor='offHireCheckbox'>
                  Off-Hire
                </label>
              </div>
             </div>
          </div>

          <h4 className='fw-bolder text-dark mb-5'>2. Position</h4>
          <div className='row mb-6'>
            <div className='col-lg-6'>
                <label className='form-label fw-bolder text-dark'>Location:</label>
                <input
                  type='text'
                  className='form-control form-control-solid'
                  placeholder='e.g. LAXMI-GAURI OIL FIELD'
                  value={positionLocation}
                  onChange={(e) => onPositionLocationChange(e.target.value)}
                />
              </div>
              <div className='col-lg-6'>
                <label className='form-label fw-bolder text-dark'>Time:</label>
                <input
                  type='time'
                  className='form-control form-control-solid'
                  value={positionTime}
                  onChange={(e) => onPositionTimeChange(e.target.value)}
                />
              </div>
          </div>

          <div className='row g-6 mb-6'>
            <div className='col-lg-6'>
              <label className='form-label fw-bolder text-dark'>
                Position Latitude <span className='text-danger'>*</span>
              </label>
              <div className='d-flex flex-wrap gap-2'>
                <div className='input-group' style={{flex: '1 1 120px'}}>
                  <input
                    type='text'
                    maxLength={8}
                    className={`form-control form-control-solid ${showErrors && !positionFields.startLatDeg ? 'border-danger' : ''}`}
                    value={positionFields.startLatDeg}
                    onChange={(e) => handlePositionFieldChange('startLatDeg', e.target.value)}
                    onBlur={handlePositionBlur}
                  />
                  <span className='input-group-text'>Deg</span>
                </div>
                <div className='input-group' style={{flex: '1 1 120px'}}>
                  <input
                    type='text'
                    maxLength={8}
                    className={`form-control form-control-solid ${showErrors && !positionFields.startLatMin ? 'border-danger' : ''}`}
                    value={positionFields.startLatMin}
                    onChange={(e) => handlePositionFieldChange('startLatMin', e.target.value)}
                    onBlur={handlePositionBlur}
                  />
                  <span className='input-group-text'>min</span>
                </div>
                <div className='input-group' style={{flex: '1 1 120px'}}>
                  <input
                    type='text'
                    maxLength={12}
                    className={`form-control form-control-solid ${showErrors && !positionFields.startLatSec ? 'border-danger' : ''}`}
                    value={positionFields.startLatSec}
                    onChange={(e) => handlePositionFieldChange('startLatSec', e.target.value)}
                    onBlur={handlePositionBlur}
                  />
                  <span className='input-group-text'>sec</span>
                </div>
                <select
                  className='form-select form-select-solid fw-bold text-center'
                  style={{ width: '65px', minWidth: '65px', flexShrink: 0 }}
                  value={positionFields.startLatDir}
                  onChange={(e) => handlePositionFieldChange('startLatDir', e.target.value)}
                >
                  <option value='N'>N</option>
                  <option value='S'>S</option>
                </select>
              </div>
              {positionErrors.startLatDeg && <div className='text-danger small mt-1'>{positionErrors.startLatDeg}</div>}
              {positionErrors.startLatMin && <div className='text-danger small mt-1'>{positionErrors.startLatMin}</div>}
              {positionErrors.startLatSec && <div className='text-danger small mt-1'>{positionErrors.startLatSec}</div>}
            </div>

            <div className='col-lg-6'>
              <label className='form-label fw-bolder text-dark'>
                Position Longitude <span className='text-danger'>*</span>
              </label>
              <div className='d-flex flex-wrap gap-2'>
                <div className='input-group' style={{flex: '1 1 120px'}}>
                  <input
                    type='text'
                    maxLength={10}
                    className={`form-control form-control-solid ${showErrors && !positionFields.startLonDeg ? 'border-danger' : ''}`}
                    value={positionFields.startLonDeg}
                    onChange={(e) => handlePositionFieldChange('startLonDeg', e.target.value)}
                    onBlur={handlePositionBlur}
                  />
                  <span className='input-group-text'>Deg</span>
                </div>
                <div className='input-group' style={{flex: '1 1 120px'}}>
                  <input
                    type='text'
                    maxLength={8}
                    className={`form-control form-control-solid ${showErrors && !positionFields.startLonMin ? 'border-danger' : ''}`}
                    value={positionFields.startLonMin}
                    onChange={(e) => handlePositionFieldChange('startLonMin', e.target.value)}
                    onBlur={handlePositionBlur}
                  />
                  <span className='input-group-text'>min</span>
                </div>
                <div className='input-group' style={{flex: '1 1 120px'}}>
                  <input
                    type='text'
                    maxLength={12}
                    className={`form-control form-control-solid ${showErrors && !positionFields.startLonSec ? 'border-danger' : ''}`}
                    value={positionFields.startLonSec}
                    onChange={(e) => handlePositionFieldChange('startLonSec', e.target.value)}
                    onBlur={handlePositionBlur}
                  />
                  <span className='input-group-text'>sec</span>
                </div>
                <select
                  className='form-select form-select-solid fw-bold text-center'
                  style={{ width: '65px', minWidth: '65px', flexShrink: 0 }}
                  value={positionFields.startLonDir}
                  onChange={(e) => handlePositionFieldChange('startLonDir', e.target.value)}
                >
                  <option value='E'>E</option>
                  <option value='W'>W</option>
                </select>
              </div>
              {positionErrors.startLonDeg && <div className='text-danger small mt-1'>{positionErrors.startLonDeg}</div>}
              {positionErrors.startLonMin && <div className='text-danger small mt-1'>{positionErrors.startLonMin}</div>}
              {positionErrors.startLonSec && <div className='text-danger small mt-1'>{positionErrors.startLonSec}</div>}
            </div>
          </div>

          <div className='row'>
            <div className='col-12'>
              <DprMiniMap lat={positionDecimals?.lat ?? null} lon={positionDecimals?.lon ?? null} height='250px' />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default VesselStatus
